/* ── Interceptor Dash Trail ───────────────────────────────────────
 * Motion-blur ribbon trail: a TubeGeometry built along the dash path
 * with a gradient alpha (head bright, tail fading). Lifetime ~1.5s.
 *
 * Path: the caller passes a `from` and `to` (or a precomputed array of
 * waypoints). We build a CatmullRomCurve3 through them and a TubeGeometry
 * with vertex colors that ramp from full alpha at the head end to zero
 * at the tail end. The whole tube fades over LIFE_SECONDS.
 *
 * Lifecycle scaffolding lives in CardSignatureEffect.
 * ───────────────────────────────────────────────────────────── */

import * as THREE from 'three/webgpu';
import { Fn, uniform, vec3, vec4, float, attribute } from 'three/tsl';
import { setEmissiveNode } from '../tslUtils';
import { CardSignatureEffect, type LivePositionResolver, type Vec3Like } from './CardSignatureEffect';

const LIFE_SECONDS = 1.5;
const TUBE_RADIUS = 0.55;
const TUBE_RADIAL_SEGMENTS = 8;
const TUBE_TUBULAR_SEGMENTS = 24;
const COLOR_HEX = 0xff8a3b;

export class InterceptorDashTrail extends CardSignatureEffect {
  static readonly LIFE_MS = LIFE_SECONDS * 1000;

  private readonly mesh: THREE.Mesh;
  private readonly geometry: THREE.TubeGeometry;
  private readonly material: THREE.MeshBasicNodeMaterial;
  private readonly uProgress: ReturnType<typeof uniform>;
  private readonly liveResolver?: LivePositionResolver;
  private readonly initialFrom = new THREE.Vector3();

  constructor(
    scene: THREE.Scene,
    from: Vec3Like,
    to: Vec3Like,
    options?: { liveResolver?: LivePositionResolver },
  ) {
    super(scene, LIFE_SECONDS);
    this.uProgress = uniform(0);
    const uColor = uniform(new THREE.Color(COLOR_HEX));
    const uProgress = this.uProgress;
    this.liveResolver = options?.liveResolver;
    this.initialFrom.set(from.x, from.y, from.z);

    const fromV = new THREE.Vector3(from.x, from.y, from.z);
    const toV = new THREE.Vector3(to.x, to.y, to.z);
    const dir = toV.clone().sub(fromV);

    // Build a 4-point spline. Even when from≈to we still get a non-degenerate
    // curve thanks to the small fallback offset.
    if (dir.lengthSq() < 1e-6) {
      dir.set(0.001, 0, 0);
    }
    const mid1 = fromV.clone().lerp(toV, 0.33);
    const mid2 = fromV.clone().lerp(toV, 0.66);
    const curve = new THREE.CatmullRomCurve3([fromV, mid1, mid2, toV]);

    this.geometry = new THREE.TubeGeometry(
      curve,
      TUBE_TUBULAR_SEGMENTS,
      TUBE_RADIUS,
      TUBE_RADIAL_SEGMENTS,
      false,
    );

    // Build a gradient alpha along the tube (tubular axis = u in the
    // generated UVs; uv.y is the radial wrap). Three.js TubeGeometry
    // sets uv such that uv.x ∈ [0,1] along the path. We bake the
    // gradient into a per-vertex `aTrail` attribute so we don't depend
    // on TSL `uv()` which on PointsMaterial gave us inconsistent results.
    const positionAttr = this.geometry.attributes.position as THREE.BufferAttribute;
    const trail = new Float32Array(positionAttr.count);
    // Each "ring" of TUBE_RADIAL_SEGMENTS+1 vertices shares the same path-u.
    const ringSize = TUBE_RADIAL_SEGMENTS + 1;
    for (let i = 0; i < positionAttr.count; i++) {
      const ring = Math.floor(i / ringSize);
      // 0 = tail (start), 1 = head (end). Trail brightness falls off toward tail.
      trail[i] = ring / TUBE_TUBULAR_SEGMENTS;
    }
    this.geometry.setAttribute('aTrail', new THREE.BufferAttribute(trail, 1));

    this.material = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    const aTrail = attribute('aTrail') as unknown as ReturnType<typeof float>;
    const colorNode = Fn(() => {
      // Bright at head (aTrail near 1), faded at tail (near 0).
      const head = float(aTrail);
      const fade = float(uProgress).oneMinus();
      const rgb = vec3(uColor).add(vec3(0.5, 0.3, 0.1).mul(head));
      const alpha = head.mul(head).mul(fade).mul(0.85);
      return vec4(rgb, alpha);
    })();
    this.material.colorNode = colorNode;
    setEmissiveNode(this.material, colorNode);

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    // Avoid frustum-cull surprises: tube length can exceed bounding-sphere estimate.
    this.mesh.frustumCulled = false;
    this.scene.add(this.mesh);
  }

  protected tick(t: number): void {
    this.uProgress.value = t;
    // Translate the whole tube to follow the caster. Geometry stays in
    // local space (built relative to the original `from`); we offset the
    // mesh's world position by the caster's delta. Cheaper than rebuilding
    // the TubeGeometry per tick.
    this.applyLiveDelta(this.mesh, this.liveResolver, this.initialFrom);
  }

  protected disposeResources(): void {
    this.scene.remove(this.mesh);
    this.geometry.dispose();
    this.material.dispose();
  }
}
